<script setup lang="ts">
import { showLoadingToast, closeToast } from 'vant'
import { useBFRepository, BFRepository } from '@/repositories/bfRepo'
import { BFAlbum, BFPicture as BFPictureType } from '@/type/BFAlbum'
import { computed, Ref, ref, onMounted } from 'vue'
import BFPicture from './BFPicture.vue'
import { getParamsFromLiffUrl } from '@/utils'
import { useResponsiveGrid, chunkGrid } from '@/composables/useResponsiveGrid'

const emptyAlbum: BFAlbum = {
  id: 0,
  name: '',
  create_by_name: '',
  update_by_name: '',
  create_date: null,
  update_date: null,
  thumbnails: [],
  tags: [''],
  patient: 0,
  patient_name: '',
  type: '',
  showcase_flag: true,
}

const { chunksize, span } = useResponsiveGrid()

let repository: BFRepository | null = null

// data
const loading: Ref<boolean> = ref(false)
const albums: Ref<BFAlbum[]> = ref([])
const cache_albums_pictures: { [key: number]: BFPictureType[] } = {}
const popup_data = ref<{ album: BFAlbum; pictures: BFPictureType[] }>({
  album: emptyAlbum,
  pictures: [],
})

const popup: Ref<boolean> = ref(false)

// computed
const table = computed(() => {
  if (!albums.value || albums.value.length == 0) return

  return chunkGrid(albums.value, chunksize.value, emptyAlbum)
})

function setCacheAlbumPictures(album_id: number, pictures: BFPictureType[]) {
  cache_albums_pictures[album_id] = pictures
}

function setLoadingToast(value: boolean) {
  if (value) {
    showLoadingToast({
      forbidClick: true,
      duration: 0,
      loadingType: 'spinner',
      overlay: true,
    })
  } else {
    closeToast()
  }
}

async function openPopup(album_id: number) {
  if (album_id === 0 || !repository) return
  setLoadingToast(true)
  const album = albums.value.find(album => album.id === album_id)
  if (album) {
    let pictures = cache_albums_pictures[album_id]
    if (!pictures) {
      // ถ้าไม่มีใน cache ให้ fetch จาก server
      pictures = await repository.fetchAlbumPicture(album_id)
      setCacheAlbumPictures(album_id, pictures)
    }
    popup_data.value = {
      album: albums.value.find(album => album.id === album_id) || emptyAlbum,
      pictures: pictures,
    }
  }
  setLoadingToast(false)
  popup.value = true
}

function closePopup() {
  popup.value = false
}

// mount
onMounted(async () => {
  loading.value = true
  const clinic_uuid = getParamsFromLiffUrl(window.location.href).get('c') || ''
  repository = useBFRepository(clinic_uuid)
  albums.value = await repository.fetchBFAlbum()
  loading.value = false
})
</script>

<template>
  <div>
    <van-divider>Showcase Album</van-divider>

    <div style="margin: 20px">
      <div v-show="loading" style="text-align: center; margin-top: 10%">
        <van-loading size="40px">Loading...</van-loading>
      </div>

      <div
        v-if="!loading && albums && albums.length === 0"
        style="text-align: center; margin-top: 10%"
      >
        <p>{{ $t('bf_showcase.no_preview_album') }}</p>
      </div>

      <van-row
        v-else
        v-for="(row, num_row) in table"
        :key="`row ${num_row}`"
        type="flex"
        gutter="20"
        justify="center"
      >
        <van-col
          v-for="(col, num_col) in row"
          :key="`col ${num_col}`"
          style="margin-top: 20px"
          :span="span"
          @click="() => openPopup(col.id)"
        >
          <div v-if="col.id === 0"></div>

          <div v-else>
            <div
              :style="{
                minHeight: 150,
                aspectRatio: 1 / 1,
                textAlign: 'center',
                borderRadius: '10px',
                border: '1px #dddddd solid',
              }"
            >
              <van-image
                radius="10"
                height="100%"
                width="100%"
                fit="cover"
                :src="col.thumbnails[0]?.thumb"
              />
            </div>
            <div style="margin-left: 20px; margin-top: 10px; text-align: left">
              <span
                style="
                  display: block;
                  overflow: hidden;
                  width: 100px;
                  text-overflow: ellipsis;
                "
                >{{ col.name }}</span
              >
              <span style="color: #aaaaaa">{{ col.thumbnails?.length }}</span>
            </div>
          </div>
        </van-col>
      </van-row>
    </div>

    <BFPicture
      @close="closePopup"
      :visible="popup"
      :album_name="popup_data.album.name"
      :pictures="popup_data.pictures"
    />
  </div>
</template>

<style scoped>
.van-grid-item__content {
  padding: 0px !important;
  background-color: black !important;
}
</style>
